import { Pencil } from 'lucide-react';
import React, { useState, useContext } from 'react';
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Input, Label, useToast } from 'randmarcomps';
import { usePostV4ManufacturerByRouteManufacturerIdCategoryAndCategoryCodeFieldNameMutation } from 'randmar-api-client';
import ManufacturerContext from '../../contexts/ManufacturerContext';

interface IProps {
  informationName: string;
  informationCurrentValue?: string;
  label?: string;
  validateFonction: (value: any) => boolean;
  categoryCode: string;
  isFloatInput: boolean;
}

const EditCategoryInformationIcon: React.FC<IProps> = ({ informationName, informationCurrentValue, label, validateFonction, categoryCode, isFloatInput }) => {
  const { applicationId } = useContext(ManufacturerContext);
  const [open, setOpen] = useState(false);
  const [informationValue, setInformationValue] = useState<any>(informationCurrentValue || "");
  const { toast } = useToast();

  const [updateCategoryProperty, { isLoading: isUpdating }] = usePostV4ManufacturerByRouteManufacturerIdCategoryAndCategoryCodeFieldNameMutation();

  const handleEditInformationClicked = () => {
    setOpen(true);
  };

  const handleConfirm = async () => {
    setOpen(false);

    try {
      await updateCategoryProperty({
        routeManufacturerId: applicationId,
        categoryCode: categoryCode,
        fieldName: informationName,
        newValue: String(informationValue),
      }).unwrap();
      toast({ variant: "success", title: "Success", description: `Property ${informationName} was successfully updated.` });
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: `Property ${informationName} failed to update.` });
    }
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const handleInformationChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (isFloatInput) {
      if (validateFonction(value) || value === "") {
        setInformationValue(value === "" ? "" : parseFloat(value));
      }
    } else {
      setInformationValue(value);
    }
  };

  return (
    <>
      <Button
        title="Edit"
        variant="ghost"
        size="icon"
        onClick={handleEditInformationClicked}
      >
        <Pencil className="h-4 w-4" />
      </Button>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleCancel()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit {label || informationName}</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor={informationName} className="text-right">
              {label || informationName}
            </Label>
            <Input
              id={informationName}
              autoFocus
              value={informationValue}
              onChange={handleInformationChanged}
              type={isFloatInput ? "number" : "text"}
              className="col-span-3"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleConfirm}
              disabled={isUpdating || !validateFonction(informationValue) || String(informationCurrentValue) === String(informationValue)}>
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EditCategoryInformationIcon;
